<template>
  <div class="update-page">
    <!-- Header Section -->
    <div class="page-header">
      <h1>Edit Ski School</h1>
      <p class="page-description">Update your ski school information</p>
    </div>

    <!-- Loading Spinner -->
    <div v-if="loading" class="loading-container">
      <div class="loading-spinner"></div>
      <p>Loading ski school...</p>
    </div>

    <div v-else-if="school" class="update-form-container">
      <div class="update-form">
        <h3>Edit Ski School</h3>
        <form @submit.prevent="updateSkiSchool">
          <div class="form-row">
            <div class="form-group">
              <label for="edit-name">School Name *</label>
              <input 
                type="text" 
                id="edit-name"
                v-model="formData.name" 
                required 
                class="form-input"
                placeholder="Enter ski school name"
              />
            </div>
            <div class="form-group">
              <label for="edit-country">Country *</label>
              <input 
                type="text" 
                id="edit-country"
                ref="editCountryInput"
                v-model="formData.country" 
                required 
                class="form-input"
                placeholder="Enter country"
              />
            </div>
          </div>

          <div class="form-group">
            <label for="edit-description">Description *</label>
            <textarea 
              id="edit-description"
              v-model="formData.description" 
              required 
              class="form-textarea"
              rows="4"
              placeholder="Describe your ski school and its offerings"
            ></textarea>
          </div>

          <!-- Logo/Profile Picture Upload for Edit -->
          <div class="form-group">
            <label for="edit-logo">School Logo/Profile Picture</label>
            <div class="file-upload-container">
              <input 
                type="file" 
                id="edit-logo"
                ref="editLogoInput"
                @change="handleEditLogoUpload"
                accept="image/*"
                class="file-input"
              />
              <label for="edit-logo" class="file-upload-button">
                <i class="fas fa-upload"></i>
                Choose New Logo Image
              </label>
              <span class="file-name" v-if="formData.logoFile">{{ formData.logoFile.name }}</span>
            </div>
            <!-- Current Image or New Preview -->
            <div v-if="formData.logoPreview || formData.logo" class="image-preview-container">
              <img 
                :src="formData.logoPreview || formData.logo" 
                alt="Logo Preview" 
                class="image-preview" 
              />
              <button type="button" @click="removeEditLogoPreview" class="remove-image-button">
                <i class="fas fa-times"></i>
              </button>
            </div>
            <small class="input-help">Recommended: Square image, max 5MB (JPEG, PNG)</small>
          </div>

          <div class="form-group">
            <label for="edit-address">Address</label>
            <input 
              type="text"
              id="edit-address"
              ref="editAddressInput"
              v-model="formData.address" 
              class="form-input"
              placeholder="Enter full address or business name"
            />
          </div>

          <div class="form-row">
            <div class="form-group">
              <label for="edit-season_start_date">Season Start Date</label>
              <input 
                type="text" 
                id="edit-season_start_date"
                v-model="formData.season_start_date" 
                class="form-input"
                placeholder="MM-DD (e.g., 12-01)"
                pattern="^(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$"
                maxlength="5"
                title="Enter date in MM-DD format (e.g., 12-01 for December 1st)"
                @input="formatDateInput($event, 'season_start_date')"
              />
              <small class="input-help">Format: MM-DD (e.g., 12-01 for December 1st)</small>
            </div>
            <div class="form-group">
              <label for="edit-season_end_date">Season End Date</label>
              <input 
                type="text" 
                id="edit-season_end_date"
                v-model="formData.season_end_date" 
                class="form-input"
                placeholder="MM-DD (e.g., 03-31)"
                pattern="^(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$"
                maxlength="5"
                title="Enter date in MM-DD format (e.g., 03-31 for March 31st)"
                @input="formatDateInput($event, 'season_end_date')"
              />
              <small class="input-help">Format: MM-DD (e.g., 03-31 for March 31st)</small>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label for="edit-price_per_set">Price per Lesson</label>
              <input 
                type="number" 
                id="edit-price_per_set"
                v-model="formData.price_per_set" 
                step="0.01"
                min="0"
                class="form-input"
                placeholder="0.00"
              />
            </div>
            <div class="form-group">
              <label for="edit-currency">Currency</label>
              <select id="edit-currency" v-model="formData.currency" class="form-select">
                <option value="USD">USD ($)</option>
                <option value="EUR">EUR (€)</option>
                <option value="GBP">GBP (£)</option>
              </select>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label for="edit-contact_email">Contact Email</label>
              <input 
                type="email" 
                id="edit-contact_email"
                v-model="formData.contact_email" 
                class="form-input"
                placeholder="contact@skischool.com"
              />
            </div>
            <div class="form-group">
              <label for="edit-contact_phone">Contact Phone</label>
              <input 
                type="tel" 
                id="edit-contact_phone"
                v-model="formData.contact_phone" 
                class="form-input"
                placeholder="+1234567890"
              />
            </div>
          </div>

          <div class="form-group">
            <label for="edit-website_url">Website URL</label>
            <input 
              type="url" 
              id="edit-website_url"
              v-model="formData.website_url" 
              class="form-input"
              placeholder="https://www.skischool.com"
            />
          </div>

          <div class="form-row">
            <div class="form-group">
              <label for="edit-instagram_url">Instagram URL</label>
              <input 
                type="url" 
                id="edit-instagram_url"
                v-model="formData.instagram_url" 
                class="form-input"
                placeholder="https://instagram.com/skischool"
              />
            </div>
            <div class="form-group">
              <label for="edit-facebook_url">Facebook URL</label>
              <input 
                type="url" 
                id="edit-facebook_url"
                v-model="formData.facebook_url" 
                class="form-input"
                placeholder="https://facebook.com/skischool"
              />
            </div>
          </div>

          <div class="form-actions">
            <button type="submit" class="submit-button" :disabled="updating">
              <i class="fas fa-save"></i>
              {{ updating ? 'Updating...' : 'Update Ski School' }}
            </button>
            <button type="button" @click="cancel" class="cancel-button">
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { API_BASE_URL } from '@/config';
import axios from 'axios';

export default {
  name: 'SkiSchoolUpdate',
  props: {
    slug: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      updating: false,
      loading: true,
      school: null,
      formData: {}
    }
  },
  async created() {
    await this.fetchSchool();
    this.loadGoogleMapsAPI();
  },
  mounted() {
    // Initialize autocomplete if Google Maps is already loaded
    if (window.google && window.google.maps && window.google.maps.places) {
      this.initializeEditAutocomplete();
    }
  },
  watch: {
    school: {
      handler(newSchool) {
        if (newSchool) {
          this.formData = { ...newSchool };
        }
      },
      immediate: true
    }
  },
  methods: {
    async fetchSchool() {
      try {
        const response = await axios.get(`${API_BASE_URL}/profiles/ski-schools/${this.slug}/`);
        this.school = response.data;
        this.formData = { ...this.school };
      } catch (error) {
        console.error('Error fetching ski school:', error);
      } finally {
        this.loading = false;
      }
    },

    async updateSkiSchool() {
      this.updating = true;
      
      try {
        const token = localStorage.getItem("access_token");
        
        // Use FormData for file uploads
        const formData = new FormData();
        
        // Add all form fields except file-related ones
        Object.keys(this.formData).forEach(key => {
          const value = this.formData[key];
          
          // Skip logoPreview and logoFile special handling
          if (key === 'logoPreview') return;
          if (key === 'logoFile') return;
          
          // Skip the existing logo field - it will be handled separately if a new file is uploaded
          if (key === 'logo') return;
          
          // Skip read-only/computed fields that shouldn't be updated
          if (key === 'coaches') return;
          if (key === 'coaches_detail') return;
          if (key === 'creator_profile') return;
          if (key === 'is_user_coach') return;
          if (key === 'total_coaches') return;
          if (key === 'season_duration_days') return;
          if (key === 'id') return;
          if (key === 'slug') return;
          if (key === 'created_at') return;
          if (key === 'updated_at') return;
          
          // Only add non-empty values
          if (value !== '' && value !== null && value !== undefined) {
            // Convert numeric fields properly
            if (key === 'price_per_set' && value !== '') {
              formData.append(key, parseFloat(value));
            }
            // Ensure coordinates are numbers
            else if ((key === 'latitude' || key === 'longitude') && value !== '') {
              formData.append(key, parseFloat(value));
            }
            else {
              formData.append(key, value);
            }
          }
        });
        
        // Add logo file if a new one was selected
        if (this.formData.logoFile) {
          formData.append('logo', this.formData.logoFile);
        }

        console.log('Updating ski school with FormData');

        const response = await axios.put(`${API_BASE_URL}/profiles/ski-schools/${this.slug}/update/`, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });

        console.log('Backend response:', response.data);

        // Update local school data
        this.school = response.data;
        this.formData = { ...response.data };
        
        alert('Ski school updated successfully!');
        
        // Navigate back to ski schools page
        this.$router.push('/ski-schools');
      } catch (error) {
        console.error('Full error object:', error);
        console.error('Error response:', error.response);
        
        if (error.response) {
          console.error('Response status:', error.response.status);
          console.error('Response data:', error.response.data);
          
          if (typeof error.response.data === 'string' && error.response.data.includes('<!DOCTYPE')) {
            alert('Server error: The backend returned an HTML error page. Check if the backend is running and the endpoint exists.');
          } else if (error.response.data) {
            const errors = Object.values(error.response.data).flat().join('\n');
            alert(`Error updating ski school:\n${errors}`);
          } else {
            alert(`HTTP ${error.response.status}: ${error.response.statusText}`);
          }
        } else {
          alert('Error updating ski school. Please try again.');
        }
      } finally {
        this.updating = false;
      }
    },

    cancel() {
      this.$router.push('/ski-schools');
    },

    initializeEditAutocomplete() {
      this.$nextTick(() => {
        // Check if Google Maps API is loaded
        if (!window.google || !window.google.maps || !window.google.maps.places) {
          console.warn('Google Maps API not loaded yet');
          return;
        }

        // Initialize edit address autocomplete
        if (this.$refs.editAddressInput) {
          const addressAutocomplete = new window.google.maps.places.Autocomplete(
            this.$refs.editAddressInput,
            {
              types: ['establishment', 'geocode'],
              fields: ['formatted_address', 'geometry', 'address_components', 'name', 'place_id']
            }
          );

          addressAutocomplete.addListener('place_changed', () => {
            const place = addressAutocomplete.getPlace();
            if (place.geometry) {
              this.formData.address = place.name && place.types?.includes('establishment') 
                ? `${place.name}, ${place.formatted_address}`
                : place.formatted_address;
              
              this.formData.latitude = parseFloat(place.geometry.location.lat().toFixed(8));
              this.formData.longitude = parseFloat(place.geometry.location.lng().toFixed(8));
              
              if (!this.formData.country && place.address_components) {
                const countryComponent = place.address_components.find(
                  component => component.types.includes('country')
                );
                if (countryComponent) {
                  this.formData.country = countryComponent.long_name;
                }
              }
              this.$forceUpdate();
            }
          });
        }

        // Initialize edit country autocomplete
        if (this.$refs.editCountryInput) {
          const countryAutocomplete = new window.google.maps.places.Autocomplete(
            this.$refs.editCountryInput,
            {
              types: ['(regions)'],
              fields: ['formatted_address', 'geometry', 'address_components']
            }
          );

          countryAutocomplete.addListener('place_changed', () => {
            const place = countryAutocomplete.getPlace();
            if (place.address_components) {
              const countryComponent = place.address_components.find(
                component => component.types.includes('country')
              );
              if (countryComponent) {
                this.formData.country = countryComponent.long_name;
                this.$forceUpdate();
              }
            }
          });
        }
      });
    },

    formatDateInput(event, field) {
      let value = event.target.value.replace(/\D/g, ''); // Remove non-digits
      
      if (value.length >= 2) {
        value = value.substring(0, 2) + '-' + value.substring(2, 4);
      }
      
      this.formData[field] = value;
    },

    handleEditLogoUpload(event) {
      const file = event.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (e) => {
          this.formData.logoPreview = e.target.result;
          this.formData.logoFile = file;
        };
        reader.readAsDataURL(file);
      }
    },

    removeEditLogoPreview() {
      this.formData.logoPreview = null;
      this.formData.logoFile = null;
    },

    loadGoogleMapsAPI() {
      // Check if Google Maps API is already loaded
      if (window.google) {
        return;
      }

      // Create script element for Google Maps API
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyC6l2P7xCZQOSH1ER0rsWDP6pSVQY5JWlw&libraries=places&callback=initGoogleMaps`;
      script.async = true;
      script.defer = true;
      
      // Set up global callback
      window.initGoogleMaps = () => {
        console.log('Google Maps API loaded');
        this.initializeEditAutocomplete();
      };
      
      document.head.appendChild(script);
    }
  }
}
</script>

<style scoped>
.update-page {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
  background-color: #15202b;
  min-height: 100vh;
}

.page-header {
  text-align: center;
  margin-bottom: 30px;
  color: #d9d9d9;
}

.page-header h1 {
  color: #1da1f2;
  font-size: 2.5em;
  margin-bottom: 10px;
}

.page-description {
  color: #8899a6;
  font-size: 1.1em;
  margin-bottom: 20px;
}

.loading-container {
  text-align: center;
  padding: 60px 20px;
  color: #8899a6;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #2a3f54;
  border-top-color: #1da1f2;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.update-form-container {
  max-width: 800px;
  margin: 0 auto 30px;
}

.update-form {
  background-color: #192734;
  padding: 30px;
  border-radius: 16px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.update-form h3 {
  color: #1da1f2;
  margin-bottom: 20px;
  text-align: center;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  color: #d9d9d9;
  margin-bottom: 8px;
  font-weight: 500;
}

.form-input,
.form-textarea,
.form-select {
  width: 100%;
  padding: 12px;
  border: 1px solid #38444d;
  border-radius: 10px;
  background-color: #15202b;
  color: #d9d9d9;
  font-size: 16px;
  transition: border-color 0.3s ease;
  box-sizing: border-box;
}

.form-input:focus,
.form-textarea:focus,
.form-select:focus {
  outline: none;
  border-color: #1da1f2;
}

.form-textarea {
  resize: vertical;
}

.input-help {
  color: #8899a6;
  font-size: 12px;
  margin-top: 4px;
  display: block;
}

.form-actions {
  display: flex;
  gap: 12px;
  justify-content: center;
  margin-top: 30px;
}

.submit-button {
  background-color: #1da1f2;
  color: #ffffff;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-weight: bold;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.submit-button:hover:not(:disabled) {
  background-color: #1a91da;
}

.submit-button:disabled {
  background-color: #2a3f54;
  cursor: not-allowed;
}

.cancel-button {
  background-color: transparent;
  color: #8899a6;
  border: 1px solid #2a3f54;
  padding: 12px 24px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.cancel-button:hover {
  background-color: #2a3f54;
  color: #d9d9d9;
}

/* File Upload Styling */
.file-upload-container {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}

.file-input {
  display: none;
}

.file-upload-button {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  background-color: #1da1f2;
  color: #ffffff;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 500;
  transition: background-color 0.3s ease;
  text-decoration: none;
}

.file-upload-button:hover {
  background-color: #1a91da;
}

.file-name {
  color: #d9d9d9;
  font-size: 14px;
  font-style: italic;
}

.image-preview-container {
  position: relative;
  display: inline-block;
  margin-top: 12px;
}

.image-preview {
  width: 120px;
  height: 120px;
  object-fit: cover;
  border-radius: 8px;
  border: 2px solid #2a3f54;
}

.remove-image-button {
  position: absolute;
  top: -8px;
  right: -8px;
  background-color: #e74c3c;
  color: #ffffff;
  border: none;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  transition: background-color 0.3s ease;
}

.remove-image-button:hover {
  background-color: #c0392b;
}

/* Mobile responsiveness */
@media (max-width: 768px) {
  .form-row {
    grid-template-columns: 1fr;
    gap: 0;
  }
  
  .update-form {
    padding: 20px;
  }
}

@media (max-width: 600px) {
  .update-form-container {
    margin: 0 auto 30px;
    padding: 0 4px;
  }
  
  .update-form {
    padding: 15px;
  }
  
  .form-input,
  .form-textarea,
  .form-select {
    box-sizing: border-box;
    width: 100%;
    font-size: 16px; /* Prevents zoom on iOS */
  }
  
  .form-actions {
    flex-direction: column;
    gap: 8px;
  }
  
  .submit-button,
  .cancel-button {
    width: 100%;
  }
}
</style> 